import { Box, Chip, IconButton, Typography } from '@mui/material';
import { ChevronUp, X } from 'lucide-react';
import { usePatientDetails } from '../../contexts/PatientDetailsContext';
import i18n from '../../i18n';
import { enumLabel } from '../../i18n/enumLabel';

export function MinimizedPatientTabs() {
  const { openPatients, restorePatient, closePatient } = usePatientDetails();

  // Filter minimized patients
  const minimizedPatients = Array.from(openPatients.entries())
    .filter(([, data]) => data.isMinimized);

  const getStatusStyle = (status?: string) => {
    switch (status) {
      case 'WAITING':
        return { bg: '#f3f4f6', color: '#6b7280', label: enumLabel('doctorQueueStatus', 'WAITING') };
      case 'IN_PROGRESS':
        return { bg: '#fef3c7', color: '#92400e', label: enumLabel('doctorQueueStatus', 'IN_PROGRESS') };
      case 'COMPLETED':
        return { bg: '#dcfce7', color: '#166534', label: enumLabel('doctorQueueStatus', 'COMPLETED') };
      case 'SKIPPED':
        return { bg: '#fed7aa', color: '#9a3412', label: enumLabel('doctorQueueStatus', 'SKIPPED') };
      case 'NO_SHOW':
        return { bg: '#fecaca', color: '#991b1b', label: enumLabel('doctorQueueStatus', 'NO_SHOW') };
      case 'CANCELLED':
        return { bg: '#fee2e2', color: '#7f1d1d', label: enumLabel('doctorQueueStatus', 'CANCELLED') };
      default:
        return { bg: '#f3f4f6', color: '#6b7280', label: i18n.t('ui:unknown') };
    }
  };

  if (minimizedPatients.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        display: 'flex',
        gap: 1,
        flexWrap: 'wrap',
        maxWidth: 'calc(100vw - 40px)',
        zIndex: 1299,
      }}
    >
      {minimizedPatients.map(([patientId, data]) => {
        const statusStyle = getStatusStyle(data.appointment?.status);
        return (
          <Box
            key={patientId}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'primary.main',
              color: 'white',
              px: 2,
              py: 1.5,
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.15)',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: 'primary.dark',
                bottom: 24,
                boxShadow: '0 -8px 20px rgba(0, 0, 0, 0.2)',
              },
            }}
            onClick={() => restorePatient(patientId)}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {data.patient.name}
                </Typography>
                {data.appointment && (
                  <Chip
                    label={statusStyle.label}
                    size="small"
                    sx={{
                      backgroundColor: statusStyle.bg,
                      color: statusStyle.color,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: '20px',
                      '& .MuiChip-label': {
                        px: '6px',
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                closePatient(patientId);
              }}
              sx={{
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                p: 0.5,
              }}
            >
              <X size={16} />
            </IconButton>
            <ChevronUp size={16} />
          </Box>
        );
      })}
    </Box>
  );
}
